/**
 * PhoneticAlphabetPopup
 *
 * Floating, non-modal NATO phonetic alphabet reference.
 * Draggable by its header so the user can park it anywhere while working.
 * Click a card to hear the word spoken aloud.
 *
 * Position is persisted to localStorage so it reopens where you left it.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { BookOpen, Volume2, X, GripHorizontal } from 'lucide-react';

const NATO: [string, string][] = [
  ['A', 'Alpha'],   ['B', 'Bravo'],    ['C', 'Charlie'], ['D', 'Delta'],
  ['E', 'Echo'],    ['F', 'Foxtrot'],  ['G', 'Golf'],    ['H', 'Hotel'],
  ['I', 'India'],   ['J', 'Juliet'],   ['K', 'Kilo'],    ['L', 'Lima'],
  ['M', 'Mike'],    ['N', 'November'], ['O', 'Oscar'],   ['P', 'Papa'],
  ['Q', 'Quebec'],  ['R', 'Romeo'],    ['S', 'Sierra'],  ['T', 'Tango'],
  ['U', 'Uniform'], ['V', 'Victor'],   ['W', 'Whiskey'], ['X', 'X-ray'],
  ['Y', 'Yankee'],  ['Z', 'Zulu'],
];

const DIGITS: [string, string][] = [
  ['0', 'Zero'],  ['1', 'One'],   ['2', 'Two'],   ['3', 'Three'], ['4', 'Four'],
  ['5', 'Five'],  ['6', 'Six'],   ['7', 'Seven'], ['8', 'Eight'], ['9', 'Nine'],
];

const POS_KEY = 'spell_helper_popup_pos';
const POPUP_W = 340;
const POPUP_H = 520;

interface Pos { x: number; y: number; }

function loadPos(): Pos {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  // Default: top-right with a bit of margin
  const x = Math.max(20, window.innerWidth - POPUP_W - 32);
  const y = 88;
  return { x, y };
}

function clampToViewport(p: Pos): Pos {
  const margin = 8;
  return {
    x: Math.min(Math.max(margin, p.x), window.innerWidth  - POPUP_W - margin),
    y: Math.min(Math.max(margin, p.y), window.innerHeight - 80     - margin), // 80 keeps header visible
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function PhoneticAlphabetPopup({ isOpen, onClose }: Props) {
  const [speaking, setSpeaking] = useState<string | null>(null);
  const [pos, setPos] = useState<Pos>(loadPos);
  const dragRef = useRef<{ startX: number; startY: number; offX: number; offY: number } | null>(null);

  // Re-clamp on viewport resize so it never drifts off-screen
  useEffect(() => {
    const onResize = () => setPos(p => clampToViewport(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const speak = useCallback((word: string, key: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(word);
    utt.rate = 0.9;
    utt.onstart = () => setSpeaking(key);
    utt.onend   = () => setSpeaking(null);
    utt.onerror = () => setSpeaking(null);
    window.speechSynthesis.speak(utt);
  }, []);

  // ── Drag handling ─────────────────────────────────────────────────────────
  const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Ignore drags that originate from buttons inside the header (close, etc.)
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offX:   e.clientX - pos.x,
      offY:   e.clientY - pos.y,
    };
  };
  const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const next = clampToViewport({
      x: e.clientX - dragRef.current.offX,
      y: e.clientY - dragRef.current.offY,
    });
    setPos(next);
  };
  const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    dragRef.current = null;
    try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch {}
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position:      'fixed',
        left:          pos.x,
        top:           pos.y,
        width:         POPUP_W,
        maxHeight:     POPUP_H,
        zIndex:        1500,
        background:    '#fff',
        borderRadius:  '14px',
        boxShadow:     '0 18px 56px rgba(0,0,0,0.22), 0 4px 14px rgba(0,0,0,0.08)',
        display:       'flex',
        flexDirection: 'column',
        overflow:      'hidden',
        animation:     'spellPopupIn 0.18s ease',
      }}
      role="dialog"
      aria-label="NATO Phonetic Alphabet"
    >
      <style>{`
        @keyframes spellPopupIn {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
      `}</style>

      {/* Drag header */}
      <div
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            '10px',
          padding:        '10px 12px 10px 14px',
          borderBottom:   '1px solid #f0f0f0',
          background:     '#fff',
          cursor:         dragRef.current ? 'grabbing' : 'grab',
          userSelect:     'none',
          touchAction:    'none',
          flexShrink:     0,
        }}
      >
        <GripHorizontal size={14} color="#c4c4c4" style={{ flexShrink: 0 }} />
        <div style={{
          width: '26px', height: '26px', borderRadius: '7px',
          background: '#f5f0ff', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <BookOpen size={13} color="#7c3aed" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#111', letterSpacing: '-0.2px' }}>
            Spell Helper
          </div>
          <div style={{ fontSize: '10.5px', color: '#a3a3a3' }}>
            Click a card to hear it
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            width: '26px', height: '26px',
            border: 'none', borderRadius: '7px',
            background: '#f5f5f5',
            cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#ebebeb')}
          onMouseLeave={e => (e.currentTarget.style.background = '#f5f5f5')}
        >
          <X size={12} color="#737373" />
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px 14px' }}>

        {/* Letters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '5px',
          marginBottom: '14px',
        }}>
          {NATO.map(([letter, word]) => {
            const isActive = speaking === letter;
            return (
              <div
                key={letter}
                onClick={() => speak(word, letter)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: isActive ? '#f5f0ff' : '#FAFAF8',
                  borderRadius: '8px', padding: '7px 10px',
                  border: `1px solid ${isActive ? '#c4b5fd' : '#f0f0f0'}`,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#f3f3f3'; }}
                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#FAFAF8'; }}
              >
                <span style={{
                  fontSize: '15px', fontWeight: 800,
                  color: isActive ? '#6d28d9' : '#7c3aed',
                  minWidth: '18px', fontFamily: 'monospace', letterSpacing: '-0.5px',
                }}>
                  {letter}
                </span>
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#262626', flex: 1 }}>
                  {word}
                </span>
                <Volume2 size={11} style={{ color: isActive ? '#7c3aed' : '#d4d4d4', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>

        {/* Digits */}
        <div style={{
          fontSize: '10px', fontWeight: 600, color: '#a3a3a3',
          letterSpacing: '0.6px', textTransform: 'uppercase',
          marginBottom: '6px', paddingLeft: '2px',
        }}>
          Numbers
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '5px',
        }}>
          {DIGITS.map(([digit, word]) => {
            const isActive = speaking === digit;
            return (
              <div
                key={digit}
                onClick={() => speak(word, digit)}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: '1px',
                  background: isActive ? '#eff6ff' : '#FAFAF8',
                  borderRadius: '8px', padding: '6px 2px',
                  border: `1px solid ${isActive ? '#bfdbfe' : '#f0f0f0'}`,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s',
                  userSelect: 'none',
                }}
                onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#f3f3f3'; }}
                onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = '#FAFAF8'; }}
              >
                <span style={{ fontSize: '14px', fontWeight: 800, color: isActive ? '#1e40af' : '#1d4ed8', fontFamily: 'monospace' }}>
                  {digit}
                </span>
                <span style={{ fontSize: '9.5px', fontWeight: 600, color: '#525252' }}>
                  {word}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
